import type {ReactElement} from 'react';
import {TextSize, TextType, Text} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './freehand-masking-editing-panel.module.scss';
import {useAppSelector} from '@/hooks';
import {
  getCreditsForImageBGR,
  isImageBGRAllowedForPAYGUser
} from '@Libraries/bg-removals-library';
import {isUserPremium} from '@Libraries/user.library';

export interface FreehandMaskingEditingPanelProps {
  isSmall: boolean;
  additionalClasses?: string;
  onEdit: (editType: FreehandMaskingEditingType) => void;
}

export enum FreehandMaskingEditingType {
  REMOVE_BACKGROUND,
  COLOR_POP,
  JOINTS,
}

function FreehandMaskingEditingPanel({...props}: FreehandMaskingEditingPanelProps): ReactElement {
  const isBGRAllowed = isUserPremium() || (isImageBGRAllowedForPAYGUser() && getCreditsForImageBGR() > 0);
  const removeBackgroundStatus = useAppSelector((state) => {
    return state.freehandMasking.backgroundRemoved;
  });
  const isRemoveBGBtnDisabled = useAppSelector((state) => {
    return state.freehandMasking.isBackgroundRemoveDisabled;
  });
  const colorPopStatus = useAppSelector((state) => {
    return state.freehandMasking.colorPop;
  });
  const jointsStatus = useAppSelector((state) => {
    return state.freehandMasking.curvedJoints;
  });

  const getRemoveBackgroundStatusText = (isActivated: boolean): string => {
    return isActivated ? window.i18next.t('pmwjs_on') : window.i18next.t('pmwjs_off');
  };

  const getColorPopStatusText = (isActivated: boolean): string => {
    return isActivated ? window.i18next.t('pmwjs_on') : window.i18next.t('pmwjs_off');
  };

  const getJointStatusText = (isActivated: boolean): string => {
    return isActivated ? window.i18next.t('pmwjs_curved') : window.i18next.t('pmwjs_straight');
  };

  const getRemoveBackgroundIcon = (): string => {
    return 'icon-remove-bg';
  };

  const getColorPopIcon = (): string => {
    return 'icon-magic-wand';
  };

  const getJointsIcon = (isActivated: boolean): string => {
    return isActivated ? 'icon-curve' : 'icon-straight';
  };

  const bigScreenPanel = (): ReactElement => {
    return (
      <ul className={`${styles.listContainer} ${props.additionalClasses ?? ''} flex-column-align-center`}>
        {getEditButton(
          getRemoveBackgroundIcon(),
          `${window.i18next.t('pmwjs_ai_background_remover')}:`,
          getRemoveBackgroundStatusText(removeBackgroundStatus),
          () => {
            props.onEdit(FreehandMaskingEditingType.REMOVE_BACKGROUND);
          },
          isRemoveBGBtnDisabled || !isBGRAllowed
        )}
        {getEditButton(getColorPopIcon(), `${window.i18next.t('pmwjs_color_pop')}:`, getColorPopStatusText(colorPopStatus), () => {
          props.onEdit(FreehandMaskingEditingType.COLOR_POP);
        })}
        {getEditButton(getJointsIcon(jointsStatus), `${window.i18next.t('pmwjs_joints')}:`, getJointStatusText(jointsStatus), () => {
          props.onEdit(FreehandMaskingEditingType.JOINTS);
        })}
      </ul>
    );
  };

  const smallScreenPanel = (): ReactElement => {
    return (
      <div className={`${props.additionalClasses ?? ''} flex-column-justify-center`}>
        <Text val="Editing Options" type={TextType.BODY} size={TextSize.SMALL} className={styles.text70} />
        <ul className={`${styles.listContainerSmall} spacing-m-t-2 flexbox flex-items-center radius-2 border-s-dark`}>
          {getEditButton(
            getRemoveBackgroundIcon(),
            `${window.i18next.t('pmwjs_ai_background_remover')}:`,
            getRemoveBackgroundStatusText(removeBackgroundStatus),
            () => {
              props.onEdit(FreehandMaskingEditingType.REMOVE_BACKGROUND);
            },
            isRemoveBGBtnDisabled || !isBGRAllowed
          )}
          {getEditButton(getColorPopIcon(), `${window.i18next.t('pmwjs_color_pop')}:`, getColorPopStatusText(colorPopStatus), () => {
            props.onEdit(FreehandMaskingEditingType.COLOR_POP);
          })}
          {getEditButton(getJointsIcon(jointsStatus), `${window.i18next.t('pmwjs_joints')}:`, getJointStatusText(jointsStatus), () => {
            props.onEdit(FreehandMaskingEditingType.JOINTS);
          })}
        </ul>
      </div>
    );
  };

  const getEditButton = (icon: string, text: string, status: string, callback: () => void, isDisabled = false): ReactElement => {
    return (
      <li className={`${styles.listItem} _full-width`}>
        <a
          className={`${styles.anchorContainer} ${props.isSmall ? '' : styles.bottomBorder} ${
            isDisabled ? styles.disabledAnchorContainer : ''
          }  spacing-p-t-4 spacing-p-b-4 flex-center flex-v-row`}
          onClick={(): void => {
            if (isDisabled) {
              console.log('disabled');
            } else {
              callback();
            }
          }}
        >
          <Icon icon={icon} size={IconSize.SIZE_ICON_24} className={styles.icon} />
          <Text val={text} type={TextType.BODY} size={TextSize.XSMALL} className={styles.text} />
          <Text val={status} type={TextType.BODY} size={TextSize.XSMALL} className={styles.text} />
        </a>
      </li>
    );
  };

  return props.isSmall ? smallScreenPanel() : bigScreenPanel();
}

export default FreehandMaskingEditingPanel;
